import type { DateTime } from 'luxon';
import type {
  CampaignDto,
  CreateCampaignDto,
} from '@/generated/korza-api-client/types.gen';
import {
  generateUtmFromName,
  validateLinkRule,
  validateRequiredTextRule,
  validateUtmCampaignRule,
} from '@/lib/campaign-fields';
import {
  parseDateParam,
  resolveLaunch,
  type ScheduleChoice,
  validateSchedule,
} from '@/lib/campaign-schedule';
import {
  parseStorePageInput,
  storeHomeUrl,
  storeLinkFields,
  type StoreLinkMode,
} from '@/lib/store-link';

export interface ComposerState {
  title: string;
  body: string;
  /** Sent as notificationLink; derived from linkMode/linkPath. */
  link: string;
  linkMode: StoreLinkMode;
  /** "Outra página" as typed (the store domain is fixed in the field). */
  linkPath: string;
  schedule: ScheduleChoice | null;
  timezone: string;
  name: string;
  nameEdited: boolean; // enquanto !nameEdited, name = title.trim()
  utmCampaign: string;
  utmEdited: boolean; // enquanto !utmEdited, utm = generateUtmFromName(name)
  audienceId: string | null;
  trackingOpen: boolean;
  utmAlreadyUsed: boolean;
  dirty: boolean;
}

export type ComposerAction =
  | {
      type: 'setTitle' | 'setBody' | 'setName' | 'setUtm';
      value: string;
    }
  | { type: 'setLinkMode'; value: StoreLinkMode; domain: string }
  /** `normalize`: a pasted/left link of the store shows as its path. */
  | { type: 'setLinkPath'; value: string; domain: string; normalize?: boolean }
  | { type: 'setSchedule'; value: ScheduleChoice | null }
  | { type: 'setTimezone'; value: string }
  | { type: 'setAudience'; value: string | null }
  | { type: 'toggleTracking' }
  | { type: 'utmAlreadyUsed' }
  | { type: 'reset'; state: ComposerState };

/** Duplicar: title/body/link/audience copiados, name "<nome> (cópia)" (nameEdited=true), utm do nome, schedule null. */
export function createInitialComposerState(input: {
  timezone: string;
  dateParam?: string | null;
  audienceIdParam?: string | null;
  duplicateOf?: CampaignDto | null;
  copySuffix: string; // "(cópia)" vindo do i18n
  storeWebsiteDomain?: string;
}): ComposerState {
  const { timezone, duplicateOf } = input;
  const domain = input.storeWebsiteDomain ?? '';

  if (duplicateOf) {
    const name = `${duplicateOf.name} ${input.copySuffix}`;
    return {
      title: duplicateOf.notificationTitle,
      body: duplicateOf.notificationBody,
      link: duplicateOf.notificationLink,
      ...storeLinkFields(duplicateOf.notificationLink, domain),
      schedule: null,
      timezone,
      name,
      nameEdited: true,
      utmCampaign: generateUtmFromName(name),
      utmEdited: false,
      audienceId: duplicateOf.audience?.id ?? null,
      trackingOpen: false,
      utmAlreadyUsed: false,
      dirty: false,
    };
  }

  return {
    title: '',
    body: '',
    // "Ao tocar, abrir" starts on the store's home page: nothing to type.
    link: storeHomeUrl(domain),
    linkMode: 'home',
    linkPath: '',
    schedule: parseDateParam(input.dateParam ?? null, timezone),
    timezone,
    name: '',
    nameEdited: false,
    utmCampaign: '',
    utmEdited: false,
    audienceId: input.audienceIdParam ?? null,
    trackingOpen: false,
    utmAlreadyUsed: false,
    dirty: false,
  };
}

/** Re-derives the name from the title and the UTM from the name while not edited. */
function withDerived(state: ComposerState): ComposerState {
  const name = state.nameEdited ? state.name : state.title.trim();
  const utmCampaign = state.utmEdited
    ? state.utmCampaign
    : generateUtmFromName(name);
  return { ...state, name, utmCampaign };
}

export function composerReducer(
  state: ComposerState,
  action: ComposerAction
): ComposerState {
  switch (action.type) {
    case 'setTitle':
      return withDerived({ ...state, title: action.value, dirty: true });
    case 'setBody':
      return { ...state, body: action.value, dirty: true };
    case 'setLinkMode':
      return {
        ...state,
        linkMode: action.value,
        link:
          action.value === 'home'
            ? storeHomeUrl(action.domain)
            : parseStorePageInput(state.linkPath, action.domain).link,
        dirty: true,
      };
    case 'setLinkPath': {
      const parsed = parseStorePageInput(action.value, action.domain);
      return {
        ...state,
        linkMode: 'page',
        linkPath: action.normalize ? parsed.path : action.value,
        link: parsed.link,
        dirty: true,
      };
    }
    case 'setName':
      return withDerived({
        ...state,
        name: action.value,
        nameEdited: true,
        dirty: true,
      });
    case 'setUtm':
      return {
        ...state,
        utmCampaign: action.value,
        utmEdited: true,
        utmAlreadyUsed: false,
        dirty: true,
      };
    case 'setSchedule':
      return { ...state, schedule: action.value, dirty: true };
    case 'setTimezone':
      return { ...state, timezone: action.value, dirty: true };
    case 'setAudience':
      return { ...state, audienceId: action.value, dirty: true };
    case 'toggleTracking':
      return { ...state, trackingOpen: !state.trackingOpen };
    case 'utmAlreadyUsed':
      return { ...state, trackingOpen: true, utmAlreadyUsed: true };
    case 'reset':
      return action.state;
  }
}

export type ComposerField =
  | 'title'
  | 'body'
  | 'link'
  | 'schedule'
  | 'name'
  | 'utmCampaign';

export interface BlockingIssue {
  field: ComposerField;
  reason: 'missing' | 'invalid';
}

/** Ordem fixa: title, body, link, schedule, name, utmCampaign. Base do "Falta: …" e do botão desativado. */
export function getBlockingIssues(
  state: ComposerState,
  ctx: { storeWebsiteDomain: string; now: DateTime }
): BlockingIssue[] {
  const issues: BlockingIssue[] = [];

  if (validateRequiredTextRule(state.title)) {
    issues.push({ field: 'title', reason: 'missing' });
  }
  if (validateRequiredTextRule(state.body)) {
    issues.push({ field: 'body', reason: 'missing' });
  }

  const linkCode = validateLinkRule(state.link, ctx.storeWebsiteDomain);
  if (linkCode) {
    issues.push({
      field: 'link',
      reason: linkCode === 'required' ? 'missing' : 'invalid',
    });
  }

  const scheduleIssue = validateSchedule(
    state.schedule,
    ctx.now,
    state.timezone
  );
  if (scheduleIssue) {
    issues.push({
      field: 'schedule',
      reason:
        scheduleIssue === 'missing' || scheduleIssue === 'timeMissing'
          ? 'missing'
          : 'invalid',
    });
  }

  if (validateRequiredTextRule(state.name)) {
    issues.push({ field: 'name', reason: 'missing' });
  }

  if (validateUtmCampaignRule(state.utmCampaign)) {
    issues.push({ field: 'utmCampaign', reason: 'missing' });
  } else if (state.utmAlreadyUsed) {
    issues.push({ field: 'utmCampaign', reason: 'invalid' });
  }

  return issues;
}

/** null se houver BlockingIssue. Resolve "Agora" com o `now` do momento do envio (edge case da spec). */
export function buildCreateCampaignPayload(
  state: ComposerState,
  ctx: { now: DateTime; storeWebsiteDomain: string; audiencesEnabled: boolean }
): CreateCampaignDto | null {
  if (getBlockingIssues(state, ctx).length > 0 || state.schedule === null) {
    return null;
  }

  const launchDateTime = resolveLaunch(
    state.schedule,
    ctx.now,
    state.timezone
  )?.toISO();
  if (!launchDateTime) return null;

  return {
    name: state.name,
    launchDateTime,
    notificationTitle: state.title,
    notificationBody: state.body,
    notificationLink: state.link,
    utmCampaign: state.utmCampaign,
    ...(ctx.audiencesEnabled && state.audienceId
      ? { audienceId: state.audienceId }
      : {}),
  };
}

export const TITLE_SOFT_LIMIT = 40; // aviso "o iPhone pode cortar"

/** Título e mensagem não vazios (AC Testar 6); o link não é exigido. */
export function canSendTest(state: ComposerState): boolean {
  return (
    validateRequiredTextRule(state.title) === null &&
    validateRequiredTextRule(state.body) === null
  );
}

/** Mesma regex da v1 (`create-campaign.page.tsx`); '/' se não casar. */
export function getTestWebsitePath(
  link: string,
  storeWebsiteDomain: string
): string {
  const escapedStoreWebsiteDomain = storeWebsiteDomain.replace(
    /[/\-\\^$*+?.()|[\]{}]/g,
    '\\$&'
  );
  const websitePath = new RegExp(
    `^https?://.*${escapedStoreWebsiteDomain}(?:/(.+))?$`
  ).exec(link)?.[1];
  return websitePath ?? '/';
}
